import { Graficas } from "src/app/graficas/gestionGraficas/graficas.model";
import { Marcadores } from "src/app/shared/model/tickets/Marcadores";

/**
 * Tipos y calculos que comparten las pestañas del Dashboard de Inicio.
 *
 * Todo es puro (sin Angular ni servicios): las pestañas consultan sus
 * datos y con esto los convierten en KPIs, donas y barras. Se extrajo tal
 * cual de dashini cuando cada pestaña paso a su propio componente, asi que
 * el comportamiento es el que ya tenia el dashboard.
 */

/** Tarjeta de indicador: valor grande, rotulo, nota y variante de color. */
export interface DashboardKpi {
  label: string;
  value: string | number;
  meta: string;
  icon: string;
  className: string;
  /**
   * Opcional: la tarjeta lleva a la pantalla que explica su número (se
   * pinta como enlace). Hoy solo «Propuestas pendientes» de Riesgos, hacia
   * «Cambios por aprobar», y solo para quien puede resolverlas.
   */
  ruta?: string;
}

/** Un segmento de dona o una barra: nombre, valor numerico y color. */
export interface DashboardGraphItem {
  nombre: string;
  valor: number;
  color: string;
}

/** Opcion del selector de periodo. */
export interface PeriodoDashboard {
  id: string;
  nombre: string;
}

/** Fechas yyyy-MM-dd con las que se arma la consulta. */
export interface RangoPeriodo {
  inicio: string;
  fin: string;
}

/** Periodos disponibles en la barra de filtros; "30" es el inicial. */
export const PERIODOS_DASHBOARD: PeriodoDashboard[] = [
  { id: "30", nombre: "Ultimos 30 dias" },
  { id: "mes", nombre: "Mes actual" },
  { id: "anio", nombre: "Anio actual" },
  { id: "todo", nombre: "Historico" },
];

/** Paleta de las graficas; se asigna por posicion (indice % largo). */
export const GRAPH_COLORS = ["#d32f2f", "#1565c0", "#00897b", "#ef6c00", "#6a5acd", "#455a64"];

/**
 * Agrupa las filas de una grafica por valor1 (en mayusculas), suma valor2
 * y asigna color por posicion. Ordena de mayor a menor, o cronologicamente
 * cuando el nombre es un mes "MM-AAAA" (ordenarPorValor = false).
 */
export function normalizarGraficas(items: Graficas[], ordenarPorValor = true): DashboardGraphItem[] {
  const acumulado = new Map<string, DashboardGraphItem>();

  [...items]
    .filter((item) => item && item.valor1 !== null && item.valor1 !== undefined)
    .forEach((item, index) => {
      const nombre = String(item.valor1 || "Sin clasificar").toUpperCase();
      const existente = acumulado.get(nombre);

      if (existente) {
        existente.valor += toNumber(item.valor2);
        return;
      }

      acumulado.set(nombre, {
        nombre,
        valor: toNumber(item.valor2),
        color: GRAPH_COLORS[index % GRAPH_COLORS.length],
      });
    });

  const normalizados = [...acumulado.values()];

  if (ordenarPorValor) {
    return normalizados.sort((a, b) => b.valor - a.valor);
  }

  return normalizados.sort((a, b) => getMonthSortKey(a.nombre) - getMonthSortKey(b.nombre));
}

/** Descarta marcadores sin nombre y los ordena de mayor a menor valor. */
export function ordenarMarcadores(items: Marcadores[]): Marcadores[] {
  return [...items]
    .filter((item) => item && item.nombre !== null && item.nombre !== undefined)
    .sort((a, b) => toNumber(b.valor) - toNumber(a.valor));
}

/** Fondo conic-gradient de la dona: un tramo en grados por cada item. */
export function buildDonutStyle(items: Array<{ color: string; valor: number }>, total: number): Record<string, string> {
  let cursor = 0;
  const segments = items.map((item) => {
    const start = cursor;
    const degrees = (item.valor / total) * 360;
    cursor += degrees;
    return `${item.color} ${start}deg ${cursor}deg`;
  });

  return { background: `conic-gradient(${segments.join(", ")})` };
}

/** Porcentaje entero de valor sobre total; 0 si no hay total. */
export function porcentaje(valor: number, total: number): number {
  if (total <= 0) {
    return 0;
  }

  return Math.round((valor / total) * 100);
}

/** Rango de fechas de un periodo del selector, siempre hasta hoy. */
export function obtenerRangoPeriodo(periodo: string): RangoPeriodo {
  const hoy = new Date();
  let inicio = new Date(hoy);

  if (periodo === "mes") {
    inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  } else if (periodo === "anio") {
    inicio = new Date(hoy.getFullYear(), 0, 1);
  } else if (periodo === "todo") {
    inicio = new Date(2020, 0, 1);
  } else {
    inicio.setDate(hoy.getDate() - 29);
  }

  return {
    inicio: toDateInput(inicio),
    fin: toDateInput(hoy),
  };
}

/** Date a "yyyy-MM-dd" en hora local. */
export function toDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** "yyyy-MM-dd" a Date local; null si el texto no es una fecha. */
export function textoAFecha(fecha: string): Date | null {
  const [anio, mes, dia] = fecha.split("-").map(Number);

  if (!anio || !mes || !dia) {
    return null;
  }

  return new Date(anio, mes - 1, dia);
}

/** Date a "yyyy-MM-dd"; cadena vacia si no hay fecha (datepicker limpio). */
export function fechaATexto(fecha: Date | null): string {
  if (!fecha) {
    return "";
  }

  return toDateInput(fecha);
}

/** Numero seguro: cualquier cosa que no sea finita vale 0. */
export function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Clave ordenable de un mes "M-AA" o "MM-AAAA" (anio * 100 + mes); 0 si no lo es. */
export function getMonthSortKey(value: string): number {
  const match = /^(\d{1,2})-(\d{2,4})$/.exec(value);

  if (!match) {
    return 0;
  }

  const month = Number(match[1]);
  const rawYear = Number(match[2]);
  const year = rawYear < 100 ? 2000 + rawYear : rawYear;

  return year * 100 + month;
}

/** Minutos a "N min", "N h" o "N h M min". */
export function formatMinutes(minutes: number): string {
  if (!minutes || minutes <= 0) {
    return "0 min";
  }

  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (hours <= 0) {
    return `${remainingMinutes} min`;
  }

  return remainingMinutes > 0 ? `${hours} h ${remainingMinutes} min` : `${hours} h`;
}
